import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { AdminClient, CreatedClientSecret } from '@baex/shared';
import { clients as clientsApi } from '../../api/endpoints';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { Modal } from '../../components/ui/Modal';
import { useBanners } from '../../components/ui/Banners';
import {
  EmptyState,
  PlainHeader,
  SkeletonRows,
  SortableHeader,
  Table,
  compareValues,
  nextSort,
} from '../../components/ui/Table';
import type { SortState } from '../../components/ui/Table';
import { cx } from '../../lib/cx';
import { formatBytes, formatCount } from '../../lib/format';
import { ClientDetailModal } from './ClientDetailModal';
import { ConfirmModal } from './ConfirmModal';
import { CreateClientModal } from './CreateClientModal';
import { DeleteClientModal } from './DeleteClientModal';
import { SecretPanel } from './SecretPanel';

/**
 * What these two columns count is not obvious from a one-word heading — the product owner
 * asked what they meant, which is the evidence. Verbatim from the "Client list — table and row actions" section.
 */
const WAITING_HELP =
  "How many files are sitting in this client's own folder, uploaded but not yet " +
  'downloaded. A file leaves storage the moment it is picked up, so this counts only what ' +
  "nobody has collected yet. A client with 'any' scope normally shows 0: it reads and " +
  'writes other clients’ folders, and nothing is addressed to its own.';

const SIZE_HELP =
  'The total size of those waiting files. This is what counts against the client’s ' +
  'storage quota, so it is the number to look at when an upload is refused with ' +
  'QUOTA_EXCEEDED. Collecting a file frees its space immediately.';

type SortKey = 'clientId' | 'displayName' | 'scope' | 'status' | 'filesWaiting' | 'bytesWaiting';

const COLUMN_COUNT = 7;

/**
 * The "Delete a client" section, point 1: Delete on an `active` client is subdued but stays clickable and
 * keyboard-reachable. This one-liner is the `title`; the dialog below carries the full
 * explanation and the way forward.
 */
export const DELETE_DISABLED_REASON = 'Disable this client before deleting it.';

/** The dialog that replaces the old (invisible) tooltip — see the "Delete a client" section, point 1, verbatim. */
export const DELETE_BLOCKED_BODY =
  'A client has to be disabled before it can be deleted. Disabling stops it uploading and ' +
  'downloading immediately and can be undone, so nothing is lost while you decide.';

export function ClientsPage() {
  const queryClient = useQueryClient();
  const banners = useBanners();

  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'clientId', direction: 'asc' });
  const [createOpen, setCreateOpen] = useState(false);
  const [viewing, setViewing] = useState<AdminClient | null>(null);
  const [disabling, setDisabling] = useState<AdminClient | null>(null);
  const [rotating, setRotating] = useState<AdminClient | null>(null);
  const [rotatedSecret, setRotatedSecret] = useState<CreatedClientSecret | null>(null);
  const [deleting, setDeleting] = useState<AdminClient | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<AdminClient | null>(null);

  const list = useQuery<AdminClient[]>({ queryKey: ['clients'], queryFn: () => clientsApi.list() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['clients'] });

  const setStatus = useMutation({
    mutationFn: (input: { clientId: string; status: 'active' | 'disabled' }) =>
      clientsApi.update(input.clientId, { status: input.status }),
    onSuccess: async (_result, input) => {
      await invalidate();
      banners.show(`${input.clientId} ${input.status === 'disabled' ? 'disabled' : 'enabled'}.`);
    },
  });

  const rows = useMemo(() => {
    const data = list.data ?? [];
    const pick = (client: AdminClient): string | number | null => client[sort.key];
    const sorted = [...data].sort((a, b) => compareValues(pick(a), pick(b)));
    return sort.direction === 'asc' ? sorted : sorted.reverse();
  }, [list.data, sort]);

  function onSort(key: SortKey) {
    setSort((current) => nextSort(current, key));
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-between">
        <h1 className="text-[15px] font-semibold text-ink">Clients</h1>
        <Button
          variant="primary"
          onClick={() => setCreateOpen(true)}
          icon={<Plus size={14} aria-hidden="true" />}
        >
          New Client
        </Button>
      </div>

      {list.isError ? (
        <ErrorNotice error={list.error} fallback="The client list could not be loaded." />
      ) : null}
      {setStatus.isError ? <ErrorNotice error={setStatus.error} /> : null}

      {/* Shrinks to its rows, and scrolls inside its own box once they outgrow the page. */}
      <Table className="min-h-[8rem]">
        <thead>
          <tr>
            <SortableHeader label="Client ID" sortKey="clientId" sort={sort} onSort={onSort} />
            <SortableHeader label="Name" sortKey="displayName" sort={sort} onSort={onSort} />
            <SortableHeader label="Scope" sortKey="scope" sort={sort} onSort={onSort} />
            <SortableHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
            <SortableHeader
              label="Waiting"
              sortKey="filesWaiting"
              sort={sort}
              onSort={onSort}
              align="right"
              className="text-right"
              help={{ label: 'What does WAITING show?', text: WAITING_HELP }}
            />
            <SortableHeader
              label="Size"
              sortKey="bytesWaiting"
              sort={sort}
              onSort={onSort}
              align="right"
              className="text-right"
              help={{ label: 'What does SIZE show?', text: SIZE_HELP }}
            />
            <PlainHeader align="right">Actions</PlainHeader>
          </tr>
        </thead>
        <tbody>
          {list.isLoading ? <SkeletonRows columns={COLUMN_COUNT} /> : null}

          {!list.isLoading && rows.length === 0 && !list.isError ? (
            <EmptyState
              title="No clients registered yet"
              hint='Use "New Client" to create the first Client ID and hand its API key to the integration owner.'
              columns={COLUMN_COUNT}
            />
          ) : null}

          {rows.map((client) => (
            /*
             * The "Client list — table and row actions" section, "Row hover and row click": the whole row is the target, it
             * highlights on hover so it reads as clickable before it is clicked, and it
             * is a keyboard target — Enter and Space open the detail view. The action
             * cell below stops events reaching this.
             *
             * Deliberately *not* `role="button"`. That replaces the row's own `row` role,
             * and with it the column-by-column navigation a screen-reader user moves
             * through a table with; the `aria-label` it needed then replaced the cells
             * too, so the row announced "View client-acme" and nothing about scope,
             * status or how many files were waiting. The Transfers table keeps native
             * row semantics and this one now matches it. The affordance is not lost: the
             * row is focusable, and the explicit **View** button in the last column is
             * the labelled control assistive technology lands on.
             */
            <tr
              key={client.clientId}
              tabIndex={0}
              onClick={() => setViewing(client)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                // Space would otherwise scroll the page out from under the dialog.
                event.preventDefault();
                setViewing(client);
              }}
              className={cx(
                'cursor-pointer border-b border-hairline last:border-0',
                'hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none',
              )}
            >
              <td className="tabular px-3 py-1.5 text-ink">{client.clientId}</td>
              <td className="max-w-[16rem] truncate px-3 py-1.5 text-ink-muted">
                {client.displayName}
              </td>
              <td className="px-3 py-1.5 text-ink-muted">{client.scope}</td>
              <td className="px-3 py-1.5 text-ink-muted">{client.status}</td>
              <td className="tabular px-3 py-1.5 text-right text-ink-muted">
                {formatCount(client.filesWaiting)}
              </td>
              <td className="tabular px-3 py-1.5 text-right text-ink-muted">
                {formatBytes(client.bytesWaiting)}
              </td>
              {/*
                Each action button does only its own thing: the activating click and key
                press stop here rather than reaching the row (see the "Client list — table and row actions" section). `Escape` and
                everything else keep travelling, so nothing else is swallowed.
              */}
              <td
                className="px-3 py-1.5"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                }}
              >
                <div className="flex items-center justify-end gap-1.5">
                  <Button size="sm" onClick={() => setViewing(client)}>
                    View
                  </Button>
                  <Button size="sm" onClick={() => setRotating(client)}>
                    Rotate key
                  </Button>
                  {client.status === 'active' ? (
                    <Button size="sm" onClick={() => setDisabling(client)}>
                      Disable
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() =>
                        setStatus.mutate({ clientId: client.clientId, status: 'active' })
                      }
                    >
                      Enable
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="danger"
                    unavailable={
                      client.status === 'active'
                        ? {
                            reason: DELETE_DISABLED_REASON,
                            onExplain: () => setDeleteBlocked(client),
                          }
                        : undefined
                    }
                    onClick={() => setDeleting(client)}
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <CreateClientModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onCreate={async (input) => {
          const created = await clientsApi.create(input);
          await invalidate();
          return created.secret;
        }}
        onDone={(clientId) => {
          setCreateOpen(false);
          banners.show(`Client ${clientId} created.`);
        }}
      />

      <ConfirmModal
        open={disabling !== null}
        title={`Disable ${disabling?.clientId ?? ''}`}
        body={`Disable ${disabling?.clientId ?? ''}? All of its API calls will start failing with 401 immediately.`}
        confirmLabel="Disable"
        busyLabel="Disabling…"
        destructive
        onCancel={() => setDisabling(null)}
        onConfirm={async () => {
          if (!disabling) return;
          await setStatus.mutateAsync({ clientId: disabling.clientId, status: 'disabled' });
          setDisabling(null);
        }}
      />

      {/* Rotate key: step 1 confirmation, then the same one-time key panel (see the "Rotate key" section). */}
      <ConfirmModal
        open={rotating !== null && rotatedSecret === null}
        title={`Rotate key for ${rotating?.clientId ?? ''}`}
        body={`Rotate the API key for ${rotating?.clientId ?? ''}? The current key will stop working immediately, and ${
          rotating?.clientId ?? ''
        } will need the new key before it can make any more calls.`}
        confirmLabel="Rotate key"
        busyLabel="Rotating…"
        onCancel={() => setRotating(null)}
        onConfirm={async () => {
          if (!rotating) return;
          const secret = await clientsApi.rotateKey(rotating.clientId);
          setRotatedSecret(secret);
          await invalidate();
        }}
      />

      {rotatedSecret ? (
        <Modal
          open
          title={`New key for "${rotatedSecret.clientId}"`}
          stepLabel="Step 2 of 2"
          footer={
            <Button
              variant="primary"
              onClick={() => {
                banners.show(`API key rotated for ${rotatedSecret.clientId}.`);
                setRotatedSecret(null);
                setRotating(null);
              }}
            >
              Done, I copied it
            </Button>
          }
        >
          <SecretPanel secret={rotatedSecret} />
        </Modal>
      ) : null}

      {/*
        Delete on an active client explains itself instead of refusing in silence, and
        offers the one step that unblocks it (see the "Delete a client" section, point 1). Disabling does **not**
        chain into the delete dialog: deleting a client is a second, deliberate decision,
        and the admin is left on the list with the row now reading `disabled`.
      */}
      <ConfirmModal
        open={deleteBlocked !== null}
        title={`Delete ${deleteBlocked?.clientId ?? ''}`}
        body={
          <>
            <strong className="font-semibold">
              {deleteBlocked?.clientId ?? ''} is still active.
            </strong>{' '}
            {DELETE_BLOCKED_BODY}
          </>
        }
        confirmLabel="Disable this client"
        busyLabel="Disabling…"
        onCancel={() => setDeleteBlocked(null)}
        onConfirm={async () => {
          if (!deleteBlocked) return;
          await setStatus.mutateAsync({ clientId: deleteBlocked.clientId, status: 'disabled' });
          setDeleteBlocked(null);
        }}
      />

      <DeleteClientModal
        client={deleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async (client) => {
          await clientsApi.remove(client.clientId);
          await invalidate();
          banners.show(
            `${client.clientId} and its ${client.filesWaiting} remaining ${
              client.filesWaiting === 1 ? 'file' : 'files'
            } have been permanently deleted.`,
          );
          setDeleting(null);
        }}
      />

      <ClientDetailModal client={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
